import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import subscriptionService from '@/services/subscriptionService'

export const useSubscriptionStore = defineStore('subscription', () => {
  // State
  const planInfo = ref(null)
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const currentPlan = computed(() => planInfo.value?.plan || 'basic')
  const isEnterprise = computed(() => planInfo.value?.plan === 'enterprise')
  const isBasic = computed(() => planInfo.value?.plan === 'basic')
  const isPlanActive = computed(() => planInfo.value?.is_active ?? false)
  
  const features = computed(() => {
    const defaultFeatures = {
      ingresos: true,
      egresos: true,
      catalogo: true,
      reportes_basicos: true,
      reportes_avanzados: false,
      contabilidad: false,
      has_contabilidad: false,
      ats: false,
      usuarios: false,
      roles_permisos: false,
      has_rrhh: false,
      has_rrhh_departamentos: false,
      has_rrhh_empleados: false,
    }

    if (!planInfo.value?.features) return defaultFeatures

    const feats = { ...defaultFeatures, ...planInfo.value.features }

    // Computar banderas de compatibilidad
    if (feats.has_accounting || feats.has_contabilidad_plan || feats.has_contabilidad_diario || feats.has_contabilidad) {
      feats.contabilidad = true
      feats.has_contabilidad = true
    }
    if (feats.has_reports_full || feats.has_reportes_generales || feats.has_reportes_detallado) {
      feats.reportes_avanzados = true
    }
    if (feats.has_user_management) {
      feats.usuarios = true
    }
    if (feats.has_roles_permissions) {
      feats.roles_permisos = true
    }
    if (feats.has_ats) {
      feats.ats = true
    }
    if (feats.has_sri_inbox || feats.sri_inbox) {
      feats.sri_inbox = true
      feats.has_sri_inbox = true
    }
    if (feats.has_purchase_entry || feats.purchase_entry) {
      feats.purchase_entry = true
      feats.has_purchase_entry = true
    }
    if (feats.has_contacts_clients || feats.contacts_clients) {
      feats.contacts_clients = true
      feats.has_contacts_clients = true
    }
    if (feats.has_contacts_suppliers || feats.contacts_suppliers) {
      feats.contacts_suppliers = true
      feats.has_contacts_suppliers = true
    }
    if (feats.has_pos || feats.has_pos_module || feats.pos_module || feats.pos || feats.has_module_pos || feats.has_pos_restaurante) {
      feats.pos = true
      feats.has_pos = true
      feats.pos_module = true
      feats.has_module_pos = true
      feats.has_pos_restaurante = true
    }

    return feats
  })

  const usage = computed(() => planInfo.value?.usage || {
    invoices_used: 0,
    invoices_limit: 50,
    invoices_remaining: 50,
  })

  const invoiceUsagePercent = computed(() => {
    if (!usage.value.invoices_limit) return 0
    return Math.round((usage.value.invoices_used / usage.value.invoices_limit) * 100)
  })

  const isNearLimit = computed(() => invoiceUsagePercent.value >= 80)
  const isAtLimit = computed(() => {
    // Si no hay límite (Enterprise), nunca está en el límite
    if (usage.value.invoices_remaining === null) return false
    // Si quedan 0 o menos, está en el límite
    return usage.value.invoices_remaining <= 0
  })

  // Actions
  async function loadPlanInfo(companyId) {
    if (!companyId || companyId === 'undefined' || companyId === 'null') return
    
    loading.value = true
    error.value = null
    
    try {
      const response = await subscriptionService.getPlanInfo(companyId)
      if (response.success) {
        planInfo.value = response.data
      }
    } catch (err) {
      console.error('Error loading plan info:', err)
      error.value = err.message
    } finally {
      loading.value = false
    }
  }

  function setPlanInfo(info) {
    planInfo.value = info
    loading.value = false
    error.value = null
  }

  function hasFeature(feature) {
    return features.value[feature] ?? false
  }

  function canAccessSection(section) {
    const sectionFeatureMap = {
      // Siempre disponibles
      'dashboard': true,
      'ingresos': true,
      'facturas': true,
      'cotizaciones': true,
      'notas-credito': true,
      'notas-debito': true,
      'egresos': true,
      'compras': true,
      'retenciones': true,
      'catalogo': true,
      'productos': true,
      'clientes': true,
      'proveedores': true,
      
      // Requieren plan enterprise
      'contabilidad': features.value.contabilidad,
      'cuentas-contables': features.value.contabilidad,
      'centros-costo': features.value.contabilidad,
      'reportes-avanzados': features.value.reportes_avanzados,
      'usuarios': features.value.usuarios,
      'roles-permisos': features.value.roles_permisos,
    }

    return sectionFeatureMap[section] ?? true
  }

  function reset() {
    planInfo.value = null
    loading.value = false
    error.value = null
  }

  return {
    // State
    planInfo,
    loading,
    error,
    
    // Getters
    currentPlan,
    isEnterprise,
    isBasic,
    isPlanActive,
    features,
    usage,
    invoiceUsagePercent,
    isNearLimit,
    isAtLimit,
    
    // Actions
    loadPlanInfo,
    setPlanInfo,
    hasFeature,
    canAccessSection,
    reset,
  }
})
